import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { X } from 'lucide-react';
import { useStore } from '@shared/hooks/useStore';
import ReaderMobileHeader from './ReaderMobileHeader';
import { TYPE_SEARCH, TypeSearch } from '@shared/types/enums';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/common/Select';

const SEARCH_TYPE_OPTIONS: { label: string, value: TypeSearch }[] = [
    { label: 'OR検索', value: TYPE_SEARCH.OR },
    { label: 'AND検索', value: TYPE_SEARCH.AND }
];

const ReaderSearchSidebar = observer(() => {
    const { readerStore } = useStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchType, setSearchType] = useState<TypeSearch>(TYPE_SEARCH.OR);

    // Sync with store's searchTerm and searchType (for URL param search)
    useEffect(() => {
        if (readerStore.searchTerm && readerStore.searchTerm !== searchTerm) {
            setSearchTerm(readerStore.searchTerm);
        }
        if (readerStore.searchType !== searchType) {
            setSearchType(readerStore.searchType);
        }
    }, [readerStore.searchTerm, readerStore.searchType]);

    const handleClear = () => {
        setSearchTerm('');
    };

    const handleSearch = () => {
        if (searchTerm.trim()) {
            readerStore.search(searchTerm, false, searchType);
        }
    };

    const handleSearchTypeChange = (value: TypeSearch) => {
        setSearchType(value);
        readerStore.setSearchType(value);
    };

    const handleResultClick = (result: any) => {
        const activeBook = readerStore.getActiveBook();
        if (activeBook) {
            readerStore.fetchPage(activeBook.publication.id, result.page_number);
            readerStore.setActiveSearchResult(result.id);

            if (window.innerWidth < 640) {
                readerStore.setIsSearchOpen(false);
            }
        }
    };

    const handleLoadMore = () => {
        readerStore.search(readerStore.searchTerm, true, searchType);
    };

    return (
        <div className='min-h-full flex flex-col w-full border border-[#FFFFFF1A] text-white p-4 gap-4'>
            {/* Header - mobile only */}
            <div className='sm:hidden'>
                <ReaderMobileHeader title='検索' onClose={() => readerStore.closeSearch()} />
            </div>

            <div>
                <div className='flex flex-col gap-2'>
                    <Select value={searchType} onValueChange={handleSearchTypeChange}>
                        <SelectTrigger className='w-full h-[28px] bg-[#FFFFFF1A] text-white border-0 rounded-[2px] focus:ring-0 focus:ring-offset-0 text-sm px-2'>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {SEARCH_TYPE_OPTIONS.map(option => (
                                <SelectItem key={option.value} value={option.value} className='text-sm h-7'>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className='flex items-center gap-1'>
                        <div className='relative flex-1'>
                            <input
                                type='text'
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                placeholder='検索'
                                className='w-full h-[28px] bg-[#FFFFFF1A] text-white pl-2 pr-8 rounded-[2px] focus:outline-none placeholder-gray-500'
                                maxLength={255}
                            />
                            {searchTerm && (
                                <button
                                    onClick={handleClear}
                                    className='absolute right-2 top-1/2 -translate-y-1/2 text-white'
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                        <button
                            onClick={handleSearch}
                            disabled={readerStore.isSearching && readerStore.searchResults.length === 0}
                            className='h-[28px] bg-primary px-6 rounded-[2px] text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            {readerStore.isSearching && readerStore.searchResults.length === 0 ? '...' : '検索'}
                        </button>
                    </div>
                </div>
            </div>

            <div className='h-[1px] w-full bg-[#FFFFFF1A]' />

            <div className='text-[11px] leading-[22px]'>
                {readerStore.searchTotal}
                件見つかりました
            </div>

            {/* Results List */}
            <div className='flex-1 overflow-y-auto custom-scrollbar'>
                {readerStore.isSearching && readerStore.searchResults.length === 0 ?
                    (
                        <div className='flex items-center justify-center pt-8'>
                            <div className='animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent'></div>
                        </div>
                    ) :
                    (
                        <div className='flex flex-col'>
                            {readerStore.searchResults?.map((result, index) => {
                                const isActive = readerStore.activeSearchResultId === result.id;
                                const itemKey = result.id || `result-${index}`;
                                return (
                                    <div key={itemKey}>
                                        <div
                                            onClick={() => handleResultClick(result)}
                                            className={`p-3 rounded cursor-pointer transition-colors group ${isActive ? 'bg-[#FFFFFF1A]' : 'hover:bg-[#FFFFFF1A]'}`}
                                        >
                                            <div className='flex justify-between items-center mb-1'>
                                                <span className='text-[11px] leading-[22px] transition-colors'>
                                                    #
                                                    {index + 1}
                                                </span>
                                                <span className='text-[11px] leading-[22px] transition-colors text-[#FFFFFF80]'>
                                                    {result.page_number}
                                                    ページ目
                                                </span>
                                            </div>
                                            <p
                                                className='text-xs leading-[26px] font-semibold line-clamp-[5]'
                                                dangerouslySetInnerHTML={{ __html: result.highlight || '' }}
                                            />
                                        </div>
                                        <div className='h-[1px] w-full bg-[#FFFFFF1A] my-4' />
                                    </div>
                                );
                            })}

                            {readerStore.searchResults.length < readerStore.searchTotal && (
                                <div className='pt-2 pb-4'>
                                    <button
                                        onClick={handleLoadMore}
                                        disabled={readerStore.isSearching}
                                        className='w-full py-2 text-[11px] font-medium text-white bg-[#FFFFFF1A] hover:bg-[#FFFFFF2A] rounded-[2px] border border-transparent transition-colors disabled:opacity-50'
                                    >
                                        {readerStore.isSearching ? '読み込み中...' : 'もっと見る'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
            </div>
        </div>
    );
});

export default ReaderSearchSidebar;
